import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Share, Plus } from "lucide-react";

/**
 * iOS Browser Blocker Component
 * 
 * Blocks app usage on iOS devices when NOT running as PWA.
 * All iOS browsers (Safari, Chrome, Firefox) use WebKit and have localStorage issues.
 * Users MUST install as PWA on iOS to avoid crashes.
 */
export default function IOSBrowserBlocker({ children }: { children: React.ReactNode }) {
  const [shouldBlock, setShouldBlock] = useState(false);

  useEffect(() => {
    // Detect if running on iOS device
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    
    // Detect if running as PWA (standalone mode)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  (window.navigator as any).standalone === true;
    
    // Block if iOS device AND not running as PWA
    if (isIOS && !isPWA) {
      setShouldBlock(true);
    }
  }, []);

  if (!shouldBlock) {
    return <>{children}</>; // Allow app to load normally
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-4 overflow-y-auto">
      <Card className="max-w-lg w-full border-destructive/50 my-8">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">PWA Installation Required</CardTitle>
          <CardDescription className="text-base space-y-2">
            <div>This app must be installed as a Progressive Web App (PWA) on iOS (iPhone or iPad) devices</div>
            <div className="mt-4">
              <div className="text-lg font-bold text-primary animate-pulse">Wega di Number Korsou</div>
              <div className="text-sm font-semibold text-primary/80 animate-pulse" style={{animationDelay: '0.3s'}}>(AI Prediction)</div>
            </div>
            <div className="text-sm font-semibold text-foreground animate-pulse" style={{animationDelay: '0.5s'}}>Created by Cyrus Brands</div>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
            <p className="text-sm text-destructive font-medium">
              ⚠️ iOS (iPhone/iPad) browsers have technical limitations that prevent this app from working correctly.
              You must install it as a PWA to use all features.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">How to Install:</h3>
            
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  1
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap the <Share className="inline w-4 h-4 mx-1" /> <strong>Share</strong> button at the bottom of Safari
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  2
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Scroll down and tap <Plus className="inline w-4 h-4 mx-1" /> <strong>"Add to Home Screen"</strong>
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  3
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap <strong>"Add"</strong> in the top right corner
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
                  4
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Open the app from your home screen
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Animated Arrow pointing to Share button - positioned above Note section */}
          <div className="flex justify-end items-center gap-2 mt-4 mb-2">
            <p className="text-xs font-semibold text-primary whitespace-nowrap">👇 Tap the 3 dots (⋮) to find Share</p>
            <svg 
              className="w-12 h-12 text-primary animate-bounce" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              style={{ transform: 'rotate(45deg)' }}
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={3} 
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </div>

          <div className="p-4 bg-muted rounded-lg space-y-2">
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> If you're using Chrome, Firefox, or another browser on iOS, 
              please open this page in Safari to install the app. All iOS (iPhone/iPad) browsers are required 
              to use Safari's engine, which has the same limitations.
            </p>
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                <strong>Need help?</strong> Contact us:
              </p>
              <p className="text-sm font-medium text-foreground mt-1">
                📞 +5999 512-2377 (Call or WhatsApp)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
